import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Building,
  Car,
  Factory,
  HeartPulse,
  Leaf,
  LucideIcon,
  MapPin,
  Rocket,
  Shield,
  Wallet,
  Wheat,
  Zap,
} from 'lucide-react';
import useFetch from './useFetch';

/**
 * Single source of truth for sector display data.
 *
 * The backend (`src/models/sectors.py`) is authoritative for the list of
 * sectors and their visual + display metadata. The frontend fetches
 * `/api/sectors` once on app boot, caches it indefinitely, and resolves the
 * icon-name string into a lucide-react component via `ICON_REGISTRY` below.
 *
 * Strict: there is no client-side fallback. `resolveSector` returns
 * `undefined` for missing or unknown names; consumers render null while
 * loading and for the transient race window. Backend `coerce_sector` is
 * the integrity boundary — it raises on unknown writes (HTTP 422), so a
 * non-canonical value never reaches the DB to begin with.
 *
 * The React Query cache is persisted via localforage / IndexedDB, so the
 * **cached** shape (`ApiSector`) must be structured-cloneable. React
 * components are NOT cloneable — that's why the icon stays a string in the
 * cache and the `LucideIcon` reference is added in a derived `useMemo`.
 *
 * Adding a sector: append to the backend `SECTOR_METADATA`. If you reuse an
 * existing `icon` value, the frontend works with no change; if you introduce
 * a new icon, add the import + entry to `ICON_REGISTRY` below.
 */

const ICON_REGISTRY: Record<string, LucideIcon> = {
  'zap': Zap,
  'rocket': Rocket,
  'wheat': Wheat,
  'map-pin': MapPin,
  'heart-pulse': HeartPulse,
  'car': Car,
  'factory': Factory,
  'leaf': Leaf,
  'building': Building,
  'wallet': Wallet,
  'shield': Shield,
};

interface ApiSector {
  name: string;
  display_name: string;
  icon: string;
  bg_color: string;
  text_color: string;
}

export interface Sector {
  /** Canonical id stored in DB / sent to the LLM (e.g. "TourismSmartCities"). */
  name: string;
  /** Human-readable label for the UI (e.g. "Tourism Tech & Smart Cities"). */
  displayName: string;
  icon: LucideIcon;
  bgColor: string;
  textColor: string;
}

// Bump the version suffix whenever the backend `SECTOR_METADATA` shape
// changes (new icon, color tweak, added sector). Persisted localforage
// entries under the old key are then ignored on next boot and a fresh
// fetch repopulates the cache — without this, returning users with a
// warm cache would never refetch (staleTime is Infinity).
const SECTORS_QUERY_KEY = ['sectors', 'v2'] as const;

function toSector(api: ApiSector): Sector {
  const icon = ICON_REGISTRY[api.icon];
  if (!icon) {
    throw new Error(
      `useSectors: backend returned unknown icon '${api.icon}' for sector ` +
        `'${api.name}'. Add it to ICON_REGISTRY in useSectors.ts.`,
    );
  }
  return {
    name: api.name,
    displayName: api.display_name,
    icon,
    bgColor: api.bg_color,
    textColor: api.text_color,
  };
}

/**
 * Returns the canonical sector list. The raw `ApiSector[]` is what lands in
 * React Query (and therefore in IndexedDB); the `Sector[]` exposed here is a
 * derived view that attaches the resolved icon component on each render.
 */
export function useSectors() {
  const { doFetch } = useFetch();
  const query = useQuery<ApiSector[]>({
    queryKey: SECTORS_QUERY_KEY,
    queryFn: () => doFetch<ApiSector[]>('/sectors'),
    staleTime: Infinity,
    gcTime: Infinity,
  });
  const data = useMemo<Sector[] | undefined>(
    () => query.data?.map(toSector),
    [query.data],
  );
  return { ...query, data };
}

/**
 * Resolve a sector by name against a list (typically the one returned by
 * `useSectors`). Pure function — safe to call inside `.map()` loops where
 * hooks can't go.
 *
 * Contract:
 * - Catalog still loading (sectors is undefined or empty) → returns
 *   ``undefined``. Callers render nothing for a frame.
 * - `name` is null/empty/unknown → returns ``undefined``. Callers render
 *   nothing. This is the safe path for the rare router/URL race; bad data
 *   coming back from the API is caught by `coerce_sector` server-side
 *   (raises a 422). There is no fallback to "Other" — the badge simply
 *   doesn't render rather than silently mislabel.
 */
export function resolveSector(
  sectors: Sector[] | undefined,
  name: string | null | undefined,
): Sector | undefined {
  if (!sectors || sectors.length === 0) return undefined;
  if (!name) return undefined;
  return sectors.find((s) => s.name === name);
}

/** Hook variant of `resolveSector` for single-name lookups in render bodies. */
export function useSector(name: string | null | undefined): Sector | undefined {
  const { data } = useSectors();
  return resolveSector(data, name);
}
